'use client';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { useSignInWithEmailPassword } from '@kit/supabase/hooks/use-sign-in-with-email-password';
import { useSignInWithOtp } from '@kit/supabase/hooks/use-sign-in-with-otp';
import { toast } from '@kit/ui/sonner';

import { useCaptcha } from '../captcha/client';
import { useLastAuthMethod } from '../hooks/use-last-auth-method';
import type { PasswordSignInSchema } from '../schemas/password-sign-in.schema';
import { AuthErrorAlert } from './auth-error-alert';
import { PasswordSignInForm } from './password-sign-in-form';

export function PasswordSignInContainer({
  onSignIn,
  defaultValues,
  magicLinkEnabled = false,
  magicLinkRedirectUrl,
}: {
  onSignIn?: (userId?: string) => unknown;
  defaultValues?: Partial<z.infer<typeof PasswordSignInSchema>>;
  /**
   * When true, the sign-in form offers a magic-link alternative behind a
   * small toggle that reuses the same email field, instead of rendering a
   * separate stacked magic-link form.
   */
  magicLinkEnabled?: boolean;
  magicLinkRedirectUrl?: string;
}) {
  const captcha = useCaptcha();
  const signInMutation = useSignInWithEmailPassword();
  const magicLinkMutation = useSignInWithOtp();
  const { recordAuthMethod } = useLastAuthMethod();
  const { t } = useTranslation();
  const isLoading = signInMutation.isPending;
  const isRedirecting = signInMutation.isSuccess;

  const onSubmit = useCallback(
    async (credentials: z.infer<typeof PasswordSignInSchema>) => {
      try {
        const data = await signInMutation.mutateAsync({
          ...credentials,
          options: { captchaToken: captcha.token },
        });

        // Record successful password sign-in
        recordAuthMethod('password', { email: credentials.email });

        if (onSignIn) {
          const userId = data?.user?.id;

          onSignIn(userId);
        }
      } catch {
        // wrong credentials, do nothing
      } finally {
        captcha.reset();
      }
    },
    [captcha, onSignIn, signInMutation, recordAuthMethod],
  );

  const onMagicLinkSubmit = useCallback(
    (email: string) => {
      const promise = async () => {
        await magicLinkMutation.mutateAsync({
          email,
          options: {
            emailRedirectTo: magicLinkRedirectUrl,
            captchaToken: captcha.token,
            shouldCreateUser: false,
          },
        });

        recordAuthMethod('magic_link', { email });
      };

      toast.promise(promise, {
        loading: t('auth:sendingEmailLink'),
        success: t('auth:sendLinkSuccessToast'),
        error: t('auth:errors.linkTitle'),
      });

      captcha.reset();
    },
    [captcha, magicLinkMutation, magicLinkRedirectUrl, recordAuthMethod, t],
  );

  return (
    <>
      <AuthErrorAlert error={signInMutation.error} />

      <PasswordSignInForm
        onSubmit={onSubmit}
        captchaLoading={!captcha.isReady}
        loading={isLoading}
        redirecting={isRedirecting}
        defaultValues={defaultValues}
        magicLinkEnabled={magicLinkEnabled}
        onMagicLinkSubmit={onMagicLinkSubmit}
        magicLinkLoading={magicLinkMutation.isPending}
      />
    </>
  );
}
